import '../index.scss';

import { useCallback, useEffect, useRef, useState } from 'react';
import { Spin } from 'antd';
import axios from 'axios';
import dayjs from 'dayjs';
import { Link } from 'react-router';

import { requestWithAuth } from '../../services/api';
import { getValue, splitNumberWithCommas } from '../../utils/general';
import CommonTable from '../../components/CommonTable';
import TopSection from '../../components/TopSection';
import ExportCsv from '../../components/ExportCsv';

interface RequestParams {
  page_size?: number;
  page?: number;
  q?: string;
}

interface FilterState {
  log_type?: string[];
}

function CreditsLog() {
  const paramsRef = useRef({});
  const [listPagination, setListPagination] = useState({
    total: 0,
    pageSize: 50,
    current: 1,
  });
  const { pageSize, current } = listPagination;
  const [isLoading, setIsLoading] = useState(false);
  //   const [sortInfo, setSortInfo] = useState({
  //     column: "",
  //     order: "",
  //   });
  const [searchQuery, setSearchQuery] = useState('');
  const [creditsData, setCreditsData] = useState([]);
  const [creditsDataCount, setCreditsDataCount] = useState(0);
  const [logTypeData, setLogTypeData] = useState([]);
  const [filterState, setFilterState] = useState<FilterState>({});
  const sourceRef = useRef(null);

  const fetchCreditsData = useCallback(() => {
    setIsLoading(true);
    const newParams: RequestParams = {
      ...paramsRef.current,
      page_size: pageSize,
      page: current,
    };
    if (searchQuery) newParams.q = searchQuery;

    if (sourceRef.current)
      sourceRef.current.cancel('Operation canceled by the user.');
    const { CancelToken } = axios; // eslint-disable-line import-x/no-named-as-default-member
    const source = CancelToken.source();
    sourceRef.current = source;

    requestWithAuth(
      'vault-credits-log',
      'GET',
      newParams,
      null,
      source.token,
    ).then((response) => {
      // A newer fetch replaced this one; let it manage the state.
      if (sourceRef.current !== source) return;
      if (response && response.results) {
        setCreditsData(response.results);
        setCreditsDataCount(response.count);
        setListPagination((prevState) => ({
          ...prevState,
          total: response.count,
        }));
      }
      setIsLoading(false);
    });
  }, [pageSize, current, searchQuery]);

  const fetchLogType = () => {
    const params = {
      key: 'log_type',
    };
    requestWithAuth('admin-field-lookup', 'GET', params, null)
      .then((response) => {
        if (response && response.result && response.result.length > 0) {
          const logTypes = response.result.map((type) => ({
            value: type,
            text: type.replaceAll('_', ' '),
          }));
          setLogTypeData([...logTypes]);
        }
      })
      .catch(function (error) {
        console.log(error);
      });
  };

  useEffect(() => {
    fetchLogType();
  }, []);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    fetchCreditsData();

    // return () => {
    //   params = {};
    // //   setSortInfo({
    // //     column: "",
    // //     order: "",
    // //   });
    //   searchQuery = "";
    // };
  }, [fetchCreditsData, filterState]);

  const handleTableChange = (pagination, filtersArg) => {
    // if (sorter && sorter.order && sorter.columnKey) {
    //   params.ordering =
    //     sorter.order === "descend" ? "-" + sorter.columnKey : sorter.columnKey;
    //   setSortInfo({
    //     column: sorter.columnKey,
    //     order: sorter.order,
    //   });
    // } else {
    //   setSortInfo({
    //     column: "",
    //     order: "",
    //   });
    //   delete params.ordering;
    // }
    // if (
    //   pagination.pageSize === listPagination.pageSize &&
    //   pagination.current === listPagination.current
    // ) {
    //   fetchCreditsData();
    // }
    setFilterState(filtersArg);
    Object.keys(filtersArg).reduce((obj, key) => {
      let value = '';
      if (filtersArg[key] !== null) value = getValue(filtersArg[key]);
      if (key === 'log_type') {
        if (!value) {
          delete paramsRef.current['type'];
        } else {
          paramsRef.current['type'] = value;
        }
      }
      return null;
    }, {});
    setListPagination({
      ...pagination,
      pageSize: pagination.pageSize,
      current: pagination.current,
    });
  };

  const handleSearchQuery = (searchValue) => {
    setSearchQuery(searchValue || '');
    setListPagination((prevState) => ({
      ...prevState,
      current: 1,
    }));
  };

  const handleFormReset = () => {
    paramsRef.current = {};
    // setSortInfo({
    //   column: "",
    //   order: "",
    // });
    setListPagination({
      current: 1,
      pageSize: 50,
      total: 0,
    });
    setSearchQuery('');
    //fetchUsersData();
  };

  const columns = [
    {
      title: 'Date',
      width: 120,
      dataIndex: 'created',
      key: 'created',
      //   sorter: true,
      //   sortDirections: ["ascend", "descend"],
      //   sortOrder: sortInfo.column === "created" && sortInfo.order,
      render: (val) => {
        return (
          <div
            style={{ width: '100%', wordBreak: 'break-word', display: 'block' }}
          >
            {val ? dayjs(val).format('MM/DD/YYYY') : ''}
          </div>
        );
      },
    },
    {
      title: 'Owner Email',
      dataIndex: 'email',
      key: 'email',
      width: 120,
    },
    // {
    //     title: 'Owner Name',
    //     dataIndex: 'owner_name',
    //     key: 'owner_name',
    //     width: 200,
    //     sorter: true,
    //     sortDirections: ['ascend', 'descend'],
    //     sortOrder: sortInfo.column === 'full_name' && sortInfo.order,
    //     className: 'word-break',
    //   },
    {
      title: 'Name of File',
      dataIndex: 'file_name',
      width: 200,
      render: (val) => {
        return (
          <div
            style={{
              width: '100%',
              wordBreak: 'break-word',
              display: 'block',
            }}
          >
            {val ? val.toString().toTitleCase() : ''}
          </div>
        );
      },
    },
    {
      title: 'Full Name',
      dataIndex: 'full_name',
      width: 200,
      render: (val) => {
        return (
          <div
            style={{
              width: '100%',
              wordBreak: 'break-word',
              display: 'block',
            }}
          >
            {val ? val.toString().toTitleCase() : ''}
          </div>
        );
      },
    },
    {
      title: 'No of Profiles',
      dataIndex: 'contacts_count',
      width: 120,
      render: (val) => {
        return <>{splitNumberWithCommas(val)}</>;
      },
    },
    {
      title: 'Credits Used',
      dataIndex: 'credits_charged',
      width: 120,
      render: (val) => {
        return <>{splitNumberWithCommas(val)}</>;
      },
    },
    {
      title: 'Log Type',
      dataIndex: 'log_type',
      width: 150,
      filters: logTypeData,
      filteredValue: filterState.log_type || [],
    },
    {
      title: 'Ledger Type',
      dataIndex: 'ledger_type',
      width: 150,
    },
    {
      title: 'Subscription Id',
      dataIndex: 'subscription_id',
      width: 150,
    },
    {
      title: 'Creator Email',
      dataIndex: 'creator_email',
      width: 150,
    },
    {
      title: 'Aid Org Name',
      dataIndex: 'organization_name',
      width: 150,
      render: (val) => {
        return val ? (
          <Link
            to={`/aid-organization?organization_name=${encodeURIComponent(val)}`}
          >
            {val}
          </Link>
        ) : (
          ''
        );
      },
    },
  ];

  const renderExportComponent = () => {
    return (
      <ExportCsv
        totalCount={creditsDataCount}
        searchQuery={searchQuery}
        url="vault-credits-log"
        downloadFileName="Credit_Table"
      />
    );
  };

  return (
    <>
      <div className="people-editor">
        <TopSection
          count={creditsDataCount}
          placeholder="Search owner email or referrer"
          showNewButton={false}
          showSearch
          handleSearchQuery={handleSearchQuery}
          handleFormReset={handleFormReset}
          showExportCsv={creditsDataCount > 0}
          renderExportComponent={renderExportComponent}
          searchValue={searchQuery}
          renderUpdateCredits={creditsDataCount > 0}
          onCreditsUpdate={fetchCreditsData}
        />
        <Spin size="default" spinning={isLoading}>
          <CommonTable
            columns={columns}
            data={creditsData}
            count={creditsDataCount}
            onTableChange={handleTableChange}
            pagination={{ ...listPagination }}
            pageSize={listPagination.pageSize}
            loading={isLoading}
            rowKey={(rec) => rec.id}
          />
        </Spin>
      </div>
    </>
  );
}

export default CreditsLog;
